import axios from 'axios';

// Use empty string for production (relative URLs) or localhost for development
const API_URL = process.env.REACT_APP_API_URL !== undefined 
  ? process.env.REACT_APP_API_URL 
  : 'http://localhost:8000';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store the getToken function reference
let getTokenFunction: (() => Promise<string | null>) | null = null;

export const setTokenGetter = (getToken: () => Promise<string | null>) => {
  getTokenFunction = getToken;
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    // Try to get fresh token from Clerk if available
    if (getTokenFunction) {
      try {
        const token = await getTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // console.log('✓ Token added to request:', config.url);
          return config;
        }
      } catch (error) {
        console.error('Failed to get fresh token:', error);
      }
    }
    
    // Fallback to sessionStorage token
    const token = sessionStorage.getItem('clerk_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✓ Token from sessionStorage added to request:', config.url);
    }
    
    // If no token is available, continue without it (for public endpoints)
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error('Unauthorized access - token may be expired');
      
      // Try to refresh token once
      if (getTokenFunction && !error.config._retry) {
        error.config._retry = true;
        try {
          const token = await getTokenFunction();
          if (token) {
            error.config.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(error.config);
          }
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
